import { Button, Card, CardBody, CardTitle, TextField } from '@fold-and-flow/ui'
import { useState } from 'react'
import { authClient } from '#lib/auth'

export function ProfilePage() {
  const { data: session } = authClient.useSession()
  const user = session?.user

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="font-serif text-3xl text-warm-gray-900">Profile</h1>
        <p className="text-warm-gray-400 mt-1">Manage your account settings</p>
      </div>

      <Card>
        <CardBody>
          <CardTitle>Account</CardTitle>
          <div className="flex flex-col gap-4 mt-4">
            <div>
              <p className="text-xs font-medium text-warm-gray-400 uppercase tracking-wide mb-1">
                Name
              </p>
              <p className="text-warm-gray-800">{user?.name ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-warm-gray-400 uppercase tracking-wide mb-1">
                Email
              </p>
              <p className="text-warm-gray-800">{user?.email ?? '—'}</p>
            </div>
          </div>
        </CardBody>
      </Card>

      <ChangePasswordCard />
    </div>
  )
}

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    if (newPassword !== confirm) {
      setError('New passwords do not match.')
      return
    }

    setIsLoading(true)
    try {
      const result = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: false,
      })

      if (result.error) {
        setError(result.error.message ?? 'Failed to change password. Check your current password.')
      } else {
        setSuccess(true)
        setCurrentPassword('')
        setNewPassword('')
        setConfirm('')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card>
      <CardBody>
        <CardTitle>Change password</CardTitle>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
          {error ? (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : null}

          {success ? (
            <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
              Password updated successfully.
            </div>
          ) : null}

          <TextField
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />

          <Button type="submit" loading={isLoading} className="self-start">
            Update password
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}
